import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { HistoriaNaMesa } from '@/historias/mesa';
import { useAbaixoDe } from '@/lib/largura';

import { carregandoA } from './carregando';
import { useLinhasNovas } from './linhaNova';
import { ContextoDaCelula, quebraDaColuna, useModoDaCelula } from './celula';

/**
 * Tabela (v2.1, p.26) — e onde o usuario passa o dia, entao as regras aqui
 * valem mais que em qualquer outro componente. Card de raio 16, cabecalho de
 * 40 px no rebaixado, linhas de 48 px, acoes que aparecem no hover.
 *
 * O que esta codificado, e nao depende de quem monta a tela:
 *
 *  - TEXTO A ESQUERDA, NUMERO A DIREITA com `tabular-nums`, STATUS A ESQUERDA.
 *    Sem o `tabular-nums` os digitos mudam de largura de linha para linha e a
 *    comparacao vertical — que e o unico motivo de existir uma coluna de
 *    numeros — se perde.
 *  - PRIMEIRA COLUNA NAO SOME AO ROLAR. Ela e a identidade da linha; rolar
 *    para a direita e nao saber mais de qual equipamento e aquela linha e o
 *    jeito classico de lancar hora no ativo errado.
 *  - SEM ZEBRA. So a divisoria de 1 px. Zebra em tabela longa cansa e briga
 *    com o fundo de linha selecionada, que e informacao de verdade.
 *  - HOVER NA LINHA INTEIRA, inclusive na celula fixa da esquerda.
 *  - SELECIONADA (p.26): linha em tinta laranja com filete de 3 px a esquerda,
 *    na celula fixa — o filete viaja com ela quando o quadro rola.
 *  - NO MAXIMO OITO COLUNAS VISIVEIS. O resto vai para o painel de detalhe.
 *  - CARREGANDO EM ESQUELETO CINZA, com a mesma altura da linha real. Nunca
 *    rodinha no meio da tela: a rodinha faz o layout pular quando o dado chega.
 *  - VAZIO COM ACAO. Tabela em branco nao existe (regra 9 do checklist).
 *  - CODIGO, NUMERO E STATUS NAO QUEBRAM (p.18); NOME E TEXTO LONGO VAO A
 *    DUAS LINHAS. Com toda celula presa numa linha, a largura minima da
 *    tabela era a soma das frases mais longas e o quadro cortava a ultima
 *    coluna no meio da palavra ("R$ 1.642,2"). Agora o texto quebra onde o
 *    quadro pedir (`quebraDaColuna`), e a linha de texto que quebra fica com
 *    os 48 px da linha dupla. O excesso que ainda sobrar vai para a rolagem
 *    lateral do quadro, com a primeira coluna fixa E COM AVISO: um degrade na
 *    borda enquanto houver coluna escondida daquele lado.
 *  - AS TRES FAIXAS DA R01 (p.28). Coluna `secundaria` sai abaixo de 1280 px
 *    (M e S: so as colunas-chave, com a rolagem lateral do quadro) — e tambem
 *    quando o QUADRO fica estreito com a janela larga (painel de detalhe
 *    aberto ao lado). Abaixo de 768 a tabela vira LISTA DE CARTOES:
 *    identidade, status e os dados-chave.
 *  - ACOES DA LINHA (p.18) a direita, aparecendo no hover e no foco, POR CIMA
 *    do fim da linha e presas a borda visivel do quadro: elas nao ocupam
 *    coluna, entao nao empurram Estado e Previsao para fora e nao ficam
 *    escondidas na parte rolada. Sempre a vista no toque e nos cartoes.
 *  - NADA DO QUADRO ALARGA A PAGINA (R01, p.28). O `sr-only` "Acoes" e
 *    `position: absolute`; sem ancestral posicionado dentro do quadro, ele
 *    escapava do recorte e esticava o documento ate a ponta da tabela — era
 *    ele, e nao a tabela, que fazia a Manutencao rolar de lado.
 */

export type AlinhamentoColuna = 'texto' | 'numero' | 'status';

export interface Coluna<T> {
  readonly chave: string;
  readonly rotulo: string;
  /** Padrao `texto`. `numero` alinha a direita e liga `tabular-nums`. */
  readonly alinhamento?: AlinhamentoColuna;
  readonly celula: (linha: T) => ReactNode;
  /** Quando existe, a tabela ganha rodape de soma nesta coluna. */
  readonly total?: (linhas: readonly T[]) => ReactNode;
  /**
   * Padrao `chave`. A `secundaria` sai abaixo de 1280 px e dos cartoes (R01,
   * p.28) — o dado continua no painel de detalhe. A primeira coluna e a
   * identidade da linha e nunca e secundaria.
   */
  readonly prioridade?: 'chave' | 'secundaria';
  /**
   * Padrao: `nunca` para `numero` e `status`, `duas-linhas` para texto (ver
   * `quebraDaColuna`). Coluna de texto que nao pode quebrar — data com hora,
   * por exemplo — declara `nunca`. No cartao, a coluna que quebra ocupa a
   * largura inteira.
   */
  readonly quebra?: 'nunca' | 'duas-linhas';
  /**
   * O codigo do modo historias (manual v2.1, p.44 a p.47) quando a coluna e um
   * elemento das tabelas do manual: o cabecalho ganha o marcador.
   */
  readonly historia?: string;
}

const MAXIMO_DE_COLUNAS = 8;

const ALINHAMENTO_CABECALHO: Record<AlinhamentoColuna, string> = {
  texto: 'text-left',
  numero: 'text-right',
  status: 'text-left',
};

/* A celula de texto nao recebe utilitario de fonte: o `body` ja e
   `--txt-corpo`, e impor `txt-corpo` aqui brigaria com o `codigo`
   (monoespacada) que a propria celula monta. */
const ALINHAMENTO_CELULA: Record<AlinhamentoColuna, string> = {
  texto: 'text-left',
  numero: 'numero',
  status: 'text-left',
};

/* Coluna secundaria: fora abaixo de 1280 px (R01, p.28) — e fora quando o
   QUADRO tem menos de 800 px com a janela larga, que e a lista de 1440 px com
   o painel de detalhe fixo ao lado (766 px). Ali a tabela tem a largura da
   faixa S e a R01 manda o conjunto de S: so as colunas-chave. O quadro e o
   `@container`; a 1280 sem painel ele tem 966 px e mostra todas, como a L
   pede. */
const SECUNDARIA = 'max-[1280px]:hidden @max-[800px]:hidden';

/* Como cada celula trata a linha (p.18; ver `quebraDaColuna`). A de texto
   quebra onde o quadro pedir, em entrelinha de 20 px e com 4 px em cima e
   embaixo: duas linhas dao os 48 px da linha dupla, e nao 50. A palavra
   longa demais (e-mail, nome de arquivo) quebra dentro dela em vez de vazar
   por cima da coluna vizinha. */
const QUEBRA_DA_CELULA = {
  nunca: 'whitespace-nowrap',
  'duas-linhas': 'whitespace-normal text-pretty leading-5 [overflow-wrap:break-word]',
} as const;

/* O FILETE DA LINHA SELECIONADA (p.26): 3 px de laranja a esquerda, por
   dentro da primeira celula. E ela que fica fixa ao rolar, entao o filete
   nunca some da vista. Literal para o Tailwind ler. */
const FILETE = 'shadow-[inset_var(--selecao-filete-largura)_0_0_var(--selecao-filete)]';

/**
 * O quadro rola de lado? E de que lado ha coluna escondida?
 *
 * Com a rolagem lateral e nada mais, a ultima coluna visivel era cortada na
 * borda no meio da palavra ("R$ 1.693,8"), e a borda cortada se lia como fim
 * da tabela. O aviso e um degrade na borda do lado em que ha mais — e some
 * quando se chega ao fim. Do lado esquerdo ele nasce na borda da coluna fixa,
 * que nunca rola.
 */
function useRolagemDoQuadro(quadro: RefObject<HTMLDivElement | null>, primeira: RefObject<HTMLElement | null>) {
  const [estado, setEstado] = useState({ antes: false, depois: false, fixa: 0 });

  useEffect(() => {
    const no = quadro.current;
    if (!no) return;
    function medir() {
      if (!no) return;
      const antes = no.scrollLeft > 1;
      const depois = no.scrollLeft + no.clientWidth < no.scrollWidth - 1;
      const fixa = primeira.current?.offsetWidth ?? 0;
      setEstado((atual) =>
        atual.antes === antes && atual.depois === depois && atual.fixa === fixa ? atual : { antes, depois, fixa },
      );
    }
    medir();
    no.addEventListener('scroll', medir, { passive: true });
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    observador?.observe(no);
    const tabela = no.firstElementChild;
    if (tabela) observador?.observe(tabela);
    return () => {
      no.removeEventListener('scroll', medir);
      observador?.disconnect();
    };
  }, [quadro, primeira]);

  return estado;
}

export function Tabela<T>({
  rotulo,
  colunas,
  linhas,
  chaveDaLinha,
  carregando = false,
  erro = null,
  vazio,
  selecionada = null,
  aoSelecionar,
  rodape = null,
  rotuloDoTotal,
  alturaDupla = false,
  acoes,
  menu,
}: {
  /** Do que e esta lista. Vira o nome acessivel da tabela. */
  readonly rotulo: string;
  readonly colunas: readonly Coluna<T>[];
  readonly linhas: readonly T[];
  readonly chaveDaLinha: (linha: T) => string;
  readonly carregando?: boolean;
  /** Estado de erro ja montado (texto que diz o que fazer + acao). */
  readonly erro?: ReactNode;
  /** Estado vazio ja montado, COM acao. Obrigatorio. */
  readonly vazio: ReactNode;
  readonly selecionada?: string | null;
  /** Quando existe, a linha inteira seleciona e a primeira celula vira botao. */
  readonly aoSelecionar?: (linha: T) => void;
  readonly rodape?: ReactNode;
  /** Texto da primeira celula do rodape de soma. */
  readonly rotuloDoTotal?: string;
  /** `true` para linha com duas informacoes (48 px em vez de 40). */
  readonly alturaDupla?: boolean;
  /**
   * As acoes da linha (p.18): ultima celula, a direita, a vista no hover e no
   * foco. Sem rotulo de coluna, e fora da conta das oito.
   */
  readonly acoes?: (linha: T) => ReactNode;
  /**
   * O menu de tres pontos ("Mais acoes"), SEMPRE a vista no canto direito da
   * linha — pedido do dono, 03/10/2026, comecando por Tanques. Ele tem largura
   * reservada, para nao cobrir o numero da ultima coluna; as `acoes` do hover
   * aparecem a esquerda dele.
   */
  readonly menu?: (linha: T) => ReactNode;
}) {
  const cartoes = useAbaixoDe(768);

  /* Verificador da invariante das oito colunas. Em desenvolvimento, porque e
     em desenvolvimento que a nona coluna e acrescentada "so para ver". */
  if (import.meta.env.DEV && colunas.length > MAXIMO_DE_COLUNAS) {
    console.error(
      `Tabela "${rotulo}": ${colunas.length} colunas. O manual (p.18) permite no máximo ${MAXIMO_DE_COLUNAS} visíveis — o resto vai para o painel de detalhe.`,
    );
  }

  const temTotal = colunas.some((coluna) => coluna.total);
  const alturaDaLinha = alturaDupla
    ? 'h-[var(--linha-tabela)]'
    : 'h-[var(--linha-tabela)]';

  if (carregando) {
    return (
      <QuadroTabela>
        <EsqueletoTabela rotulo={rotulo} colunas={colunas} alturaDupla={alturaDupla} comRodape={rodape !== null} />
      </QuadroTabela>
    );
  }

  if (erro) {
    return <QuadroTabela>{erro}</QuadroTabela>;
  }

  if (linhas.length === 0) {
    return <QuadroTabela>{vazio}</QuadroTabela>;
  }

  if (cartoes) {
    return (
      <QuadroTabela>
        <ListaDeCartoes
          rotulo={rotulo}
          colunas={colunas}
          linhas={linhas}
          chaveDaLinha={chaveDaLinha}
          selecionada={selecionada}
          {...(aoSelecionar ? { aoSelecionar } : {})}
          {...(acoes ? { acoes } : {})}
          {...(menu ? { menu } : {})}
          {...(rotuloDoTotal === undefined ? {} : { rotuloDoTotal })}
        />
        {rodape}
      </QuadroTabela>
    );
  }

  return (
    <QuadroTabela>
      <TabelaLarga
        rotulo={rotulo}
        colunas={colunas}
        linhas={linhas}
        chaveDaLinha={chaveDaLinha}
        selecionada={selecionada}
        alturaDaLinha={alturaDaLinha}
        temTotal={temTotal}
        {...(aoSelecionar ? { aoSelecionar } : {})}
        {...(acoes ? { acoes } : {})}
        {...(menu ? { menu } : {})}
        {...(rotuloDoTotal === undefined ? {} : { rotuloDoTotal })}
      />
      {rodape}
    </QuadroTabela>
  );
}

/**
 * A tabela de 768 px para cima. Componente proprio porque a medida da rolagem
 * precisa do quadro MONTADO: com carregando, vazio e cartoes o quadro rolante
 * nem existe.
 */
function TabelaLarga<T>({
  rotulo,
  colunas,
  linhas,
  chaveDaLinha,
  selecionada,
  aoSelecionar,
  alturaDaLinha,
  temTotal,
  rotuloDoTotal,
  acoes,
  menu,
}: {
  readonly rotulo: string;
  readonly colunas: readonly Coluna<T>[];
  readonly linhas: readonly T[];
  readonly chaveDaLinha: (linha: T) => string;
  readonly selecionada: string | null;
  readonly aoSelecionar?: (linha: T) => void;
  readonly alturaDaLinha: string;
  readonly temTotal: boolean;
  readonly rotuloDoTotal?: string;
  readonly acoes?: (linha: T) => ReactNode;
  readonly menu?: (linha: T) => ReactNode;
}) {
  const quadro = useRef<HTMLDivElement>(null);
  const primeira = useRef<HTMLTableCellElement>(null);
  const rolagem = useRolagemDoQuadro(quadro, primeira);
  const novas = useLinhasNovas(linhas.map(chaveDaLinha));

  return (
    <div className="relative">
      {/* A rolagem horizontal mora aqui: e ela que faz a primeira coluna
          `sticky` ter de que se fixar. `relative` para ser o bloco de
          contencao de todo descendente absoluto — o `sr-only` do cabecalho das
          acoes escapava do recorte e alargava a pagina (R01, p.28). */}
      <div ref={quadro} className="relative overflow-x-auto">
        {/* `border-separate` com espacamento zero, e nao `border-collapse`:
            com bordas colapsadas o Chrome deixa a divisoria para tras quando a
            primeira coluna gruda, e a coluna fixa aparece sem as linhas
            horizontais. Com bordas separadas, a divisoria pertence a celula e
            viaja com ela. Por isso o `border-t` esta em cada `td`, e nao no
            `tr` — no modo separado, borda em `tr` simplesmente nao pinta. */}
        <table className="w-full border-separate border-spacing-0" aria-label={rotulo}>
          <thead>
            <tr className="h-[var(--tabela-cabecalho)] bg-rebaixada">
              {colunas.map((coluna, indice) => (
                <th
                  key={coluna.chave}
                  ref={indice === 0 ? primeira : undefined}
                  scope="col"
                  className={`txt-legenda whitespace-nowrap px-3 text-tinta-secundaria ${ALINHAMENTO_CABECALHO[coluna.alinhamento ?? 'texto']} ${indice === 0 ? 'sticky left-0 z-10 bg-rebaixada' : ''} ${indice > 0 && coluna.prioridade === 'secundaria' ? SECUNDARIA : ''}`}
                >
                  {coluna.historia ? <HistoriaNaMesa codigo={coluna.historia}>{coluna.rotulo}</HistoriaNaMesa> : coluna.rotulo}
                </th>
              ))}
              {acoes || menu ? (
                /* Sem largura: a coluna das acoes nao existe para a conta do
                   quadro. O rotulo e so para o leitor de tela. Com o menu de
                   tres pontos, ela reserva a largura dele (48 px). */
                <th scope="col" className={`sticky right-0 p-0 text-right ${menu ? 'w-12' : 'w-0'}`}>
                  <span className="sr-only">Ações</span>
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {linhas.map((linha) => {
              const chave = chaveDaLinha(linha);
              const estaSelecionada = chave === selecionada;

              /* A celula fixa precisa repetir o fundo da linha: como ela flutua
                 por cima das outras ao rolar, fundo transparente deixaria o
                 conteudo de baixo aparecer atraves dela. */
              const fundoDaCelulaFixa = estaSelecionada
                ? 'bg-selecao'
                : 'bg-card group-hover:bg-[var(--superficie-rebaixada)]';

              return (
                <tr
                  key={chave}
                  {...(estaSelecionada ? { 'aria-selected': true } : {})}
                  onClick={aoSelecionar ? () => aoSelecionar(linha) : undefined}
                  className={`group ${alturaDaLinha} ${
                    estaSelecionada ? 'bg-selecao' : 'hover:bg-[var(--superficie-rebaixada)]'
                  } ${aoSelecionar ? 'cursor-pointer' : ''} ${novas.has(chave) ? 'linha-nova' : ''}`}
                >
                  {colunas.map((coluna, indice) => {
                    const conteudo = coluna.celula(linha);

                    return (
                      <td
                        key={coluna.chave}
                        className={`border-t border-borda-sutil px-3 py-1 ${QUEBRA_DA_CELULA[quebraDaColuna(coluna)]} ${ALINHAMENTO_CELULA[coluna.alinhamento ?? 'texto']} ${indice === 0 ? `sticky left-0 ${fundoDaCelulaFixa}` : ''} ${indice > 0 && coluna.prioridade === 'secundaria' ? SECUNDARIA : ''} ${estaSelecionada && indice === 0 ? FILETE : ''}`}
                      >
                        {indice === 0 && aoSelecionar ? (
                          /* Botao de verdade na celula de identidade: e por ele
                             que se abre o detalhe pelo teclado. Linha clicavel
                             sem isso e uma tela que so funciona com mouse. */
                          <button
                            type="button"
                            className="cursor-pointer text-left"
                            onClick={(evento) => {
                              evento.stopPropagation();
                              aoSelecionar(linha);
                            }}
                          >
                            {conteudo}
                          </button>
                        ) : (
                          conteudo
                        )}
                      </td>
                    );
                  })}
                  {acoes || menu ? (
                    <td
                      /* Presa a borda direita VISIVEL do quadro e sem largura:
                         as acoes sao desenhadas por cima do fim da linha, no
                         fundo dela. Sem mouse (toque), voltam a ocupar a
                         celula e ficam sempre a vista. */
                      className={`sticky right-0 z-30 border-t border-borda-sutil p-0 ${menu ? 'w-12' : 'w-0'} [@media(hover:none)]:w-auto [@media(hover:none)]:px-4`}
                      onClick={(evento) => {
                        /* Acao da linha nao seleciona a linha. */
                        evento.stopPropagation();
                      }}
                    >
                      {/* Aparecem no hover e no foco (p.18); sem mouse, sempre.
                          Escondidas, nao recebem o clique: o fim da linha
                          continua selecionando a linha. */}
                      {acoes ? (
                      <span
                        className={`pointer-events-none absolute inset-y-0 ${menu ? 'right-12' : 'right-0'} flex items-center justify-end gap-2 whitespace-nowrap px-4 opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:static [@media(hover:none)]:opacity-100 [@media(hover:none)]:px-0 ${
                          estaSelecionada ? 'bg-selecao' : 'bg-card group-hover:bg-[var(--superficie-rebaixada)]'
                        }`}
                      >
                        {acoes(linha)}
                      </span>
                      ) : null}
                      {/* O menu de tres pontos: sempre a vista, no fim da linha. */}
                      {menu ? (
                        <span
                          className={`relative flex h-full items-center justify-center [@media(hover:none)]:inline-flex ${
                            estaSelecionada ? 'bg-selecao' : 'bg-card group-hover:bg-[var(--superficie-rebaixada)]'
                          }`}
                        >
                          {menu(linha)}
                        </span>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>

          {temTotal ? (
            <tfoot>
              <tr className={`${alturaDaLinha} bg-rebaixada`}>
                {colunas.map((coluna, indice) => (
                  <td
                    key={coluna.chave}
                    className={`whitespace-nowrap border-t border-borda bg-rebaixada px-3 ${ALINHAMENTO_CELULA[coluna.alinhamento ?? 'texto']} ${indice === 0 ? 'sticky left-0' : ''} ${indice > 0 && coluna.prioridade === 'secundaria' ? SECUNDARIA : ''}`}
                  >
                    {coluna.total ? (
                      coluna.total(linhas)
                    ) : indice === 0 && rotuloDoTotal ? (
                      <span className="txt-legenda text-tinta-secundaria">{rotuloDoTotal}</span>
                    ) : null}
                  </td>
                ))}
                {acoes || menu ? <td className={`${menu ? 'w-12' : 'w-0'} border-t border-borda bg-rebaixada p-0`} /> : null}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {/* O AVISO DE QUE HA MAIS (pedido do dono: "rolar com indicacao clara,
          nunca cortar palavra sem aviso"). Degrade na cor do quadro, por cima
          da borda do lado em que ha coluna escondida; some no fim da
          rolagem. Nao recebe clique. */}
      {rolagem.depois ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-20 w-12 bg-linear-to-l from-card to-transparent"
        />
      ) : null}
      {rolagem.antes ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 z-20 w-12 bg-linear-to-r from-card to-transparent"
          style={{ left: rolagem.fixa }}
        />
      ) : null}
    </div>
  );
}

/**
 * O quadro da tabela. E um `@container`: a coluna secundaria sai pela largura
 * DELE quando a janela e larga mas o quadro nao (painel de detalhe aberto).
 */
export function QuadroTabela({ children }: { readonly children: ReactNode }) {
  return <div className="cartao @container overflow-hidden">{children}</div>;
}

/**
 * A tabela abaixo de 768 px (R01, p.28: "vira lista de cartoes").
 *
 * Um cartao por linha, com o que a tabela ja sabe de cada coluna: a primeira
 * e a identidade (titulo do cartao, e o botao que abre o detalhe), as de
 * status vem logo abaixo dela, e as outras colunas-chave viram pares rotulo e
 * valor. A secundaria fica de fora, como na tabela abaixo de 1280 — ela esta
 * no painel de detalhe. A mesma selecao, o mesmo contorno, e as acoes da
 * linha sempre a vista: nao ha hover no toque.
 *
 * NO CARTAO NADA E CORTADO. Ele tem a altura que a tabela nao tem, entao:
 *  - o status (selo e motivo) ganha LINHA PROPRIA, na largura inteira — preso
 *    ao lado do titulo, o motivo cabia na largura do selo ("Transporte d…");
 *  - texto ocupa as DUAS colunas do cartao; numero e o que nao quebra dividem
 *    a linha, dois a dois;
 *  - o que a celula trouxe cortado para a tabela (`truncate`) volta a quebrar
 *    linha, o numero alinha a esquerda como o rotulo acima dele, e a palavra
 *    sem ponto de quebra (e-mail, nome de arquivo) quebra dentro da caixa em
 *    vez de passar por cima do vizinho;
 *  - a celula fica sabendo que esta num cartao (`useModoDaCelula`).
 */
const CELULA_DO_CARTAO =
  'min-w-0 [overflow-wrap:anywhere] [&_.numero]:text-left [&_.numero]:whitespace-normal [&_.truncate]:overflow-visible [&_.truncate]:whitespace-normal';

function ListaDeCartoes<T>({
  rotulo,
  colunas,
  linhas,
  chaveDaLinha,
  selecionada,
  aoSelecionar,
  acoes,
  menu,
  rotuloDoTotal,
}: {
  readonly rotulo: string;
  readonly colunas: readonly Coluna<T>[];
  readonly linhas: readonly T[];
  readonly chaveDaLinha: (linha: T) => string;
  readonly selecionada: string | null;
  readonly aoSelecionar?: (linha: T) => void;
  readonly acoes?: (linha: T) => ReactNode;
  readonly menu?: (linha: T) => ReactNode;
  readonly rotuloDoTotal?: string;
}) {
  const [identidade, ...resto] = colunas;
  const chaves = resto.filter((coluna) => coluna.prioridade !== 'secundaria');
  const status = chaves.filter((coluna) => coluna.alinhamento === 'status');
  const dados = chaves.filter((coluna) => coluna.alinhamento !== 'status');
  const totais = colunas.filter((coluna) => coluna.total);
  /* Texto na largura inteira do cartao; o que nao quebra, em meia. */
  const vao = (coluna: Coluna<T>) => (quebraDaColuna(coluna) === 'duas-linhas' ? 'col-span-2' : '');
  const novas = useLinhasNovas(linhas.map(chaveDaLinha));

  return (
    <ContextoDaCelula.Provider value="cartao">
      <ul aria-label={rotulo} className="flex flex-col">
        {linhas.map((linha) => {
          const chave = chaveDaLinha(linha);
          const estaSelecionada = chave === selecionada;
          const titulo = identidade ? identidade.celula(linha) : null;
          return (
            <li
              key={chave}
              {...(estaSelecionada ? { 'aria-current': true } : {})}
              onClick={aoSelecionar ? () => aoSelecionar(linha) : undefined}
              className={`flex flex-col gap-3 border-t border-borda-sutil p-4 first:border-t-0 ${
                estaSelecionada ? `bg-selecao ${FILETE}` : ''
              } ${aoSelecionar ? 'cursor-pointer' : ''} ${novas.has(chave) ? 'linha-nova' : ''}`}
            >
              <div className={CELULA_DO_CARTAO}>
                {aoSelecionar ? (
                  <button
                    type="button"
                    className="max-w-full cursor-pointer text-left"
                    onClick={(evento) => {
                      evento.stopPropagation();
                      aoSelecionar(linha);
                    }}
                  >
                    {titulo}
                  </button>
                ) : (
                  titulo
                )}
              </div>
              {status.length > 0 ? (
                <div className={`flex flex-col items-start gap-1 ${CELULA_DO_CARTAO}`}>
                  {status.map((coluna) => (
                    <div key={coluna.chave} className="w-full min-w-0">
                      {coluna.celula(linha)}
                    </div>
                  ))}
                </div>
              ) : null}

              {dados.length > 0 ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {dados.map((coluna) => (
                    <div key={coluna.chave} className={`flex min-w-0 flex-col gap-1 ${vao(coluna)}`}>
                      <dt className="txt-legenda text-tinta-secundaria">{coluna.rotulo}</dt>
                      <dd className={CELULA_DO_CARTAO}>{coluna.celula(linha)}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {acoes || menu ? (
                <div
                  className="flex flex-wrap items-center justify-end gap-2"
                  onClick={(evento) => {
                    evento.stopPropagation();
                  }}
                >
                  {acoes ? acoes(linha) : null}
                  {menu ? menu(linha) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {totais.length > 0 ? (
        <div className="flex flex-col gap-3 border-t border-borda bg-rebaixada p-4">
          {rotuloDoTotal ? <span className="txt-legenda text-tinta-secundaria">{rotuloDoTotal}</span> : null}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {totais.map((coluna) => (
              <div key={coluna.chave} className="flex min-w-0 flex-col gap-1">
                <dt className="txt-legenda text-tinta-secundaria">{coluna.rotulo}</dt>
                <dd className={CELULA_DO_CARTAO}>{coluna.total?.(linhas)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </ContextoDaCelula.Provider>
  );
}

/**
 * Texto de celula: nome, obra, descricao, frase de situacao (p.18).
 *
 * NA TABELA vai a no maximo `linhas` linhas (duas, por padrao) e so corta com
 * reticencias alem disso, com o texto inteiro no `title` — e no painel de
 * detalhe, que e onde o texto longo mora. A largura sai do CONTEUDO, com teto
 * de 40 caracteres: a coluna pede o que o texto precisa, e so encolhe (e
 * quebra) quando o quadro nao comporta todas. Era o que o `w-0 min-w-full`
 * dos modulos nao fazia: ele tirava o texto da conta, as colunas recebiam
 * fatias iguais e o texto saia cortado mesmo com a tela sobrando a 1920.
 *
 * NO CARTAO (abaixo de 768 px) nao corta nunca: ha altura para o texto
 * inteiro.
 *
 * `classe` leva o tamanho e a cor (`txt-corpo text-tinta`, `txt-apoio
 * text-tinta-apagada`...).
 */
export function TextoDaCelula({
  texto,
  classe = '',
  linhas = 2,
}: {
  readonly texto: string;
  readonly classe?: string;
  readonly linhas?: 1 | 2;
}) {
  const modo = useModoDaCelula();
  if (modo === 'cartao') {
    return <span className={`block text-pretty [overflow-wrap:anywhere] ${classe}`}>{texto}</span>;
  }
  return (
    <span
      className={`block max-w-[40ch] whitespace-normal text-pretty [overflow-wrap:break-word] ${linhas === 1 ? 'line-clamp-1' : 'line-clamp-2'} ${classe}`}
      title={texto}
    >
      {texto}
    </span>
  );
}

/** O que o esqueleto precisa saber de uma coluna: o nome e o tipo, nunca a celula. */
export type ColunaDoEsqueleto = Pick<Coluna<unknown>, 'chave' | 'rotulo' | 'alinhamento' | 'prioridade'>;

/* Larguras que variam de linha para linha (multiplos de 4 px): codigo e nome
   de verdade nao terminam todos no mesmo ponto, e barras iguais leem como
   grade vazia, nao como lista. */
const LARGURA_DA_IDENTIDADE = ['w-32', 'w-24', 'w-28', 'w-36'] as const;
const LARGURA_DE_TEXTO = ['w-24', 'w-16', 'w-20', 'w-28'] as const;
const LARGURA_DE_NUMERO = ['w-12', 'w-16', 'w-10', 'w-12'] as const;

function BarraDaCelula({
  alinhamento,
  coluna,
  linha,
  alturaDupla,
}: {
  readonly alinhamento: AlinhamentoColuna;
  readonly coluna: number;
  readonly linha: number;
  readonly alturaDupla: boolean;
}) {
  /* Status e selo (p.24): a forma dele e a pilula de 24 px, e nao uma linha. */
  if (alinhamento === 'status') return <span className="esqueleto block h-6 w-24 rounded-pill" />;
  if (alinhamento === 'numero') {
    return <span className={`esqueleto ml-auto block h-3 ${LARGURA_DE_NUMERO[linha % 4] ?? 'w-12'}`} />;
  }
  const largura =
    coluna === 0 ? (LARGURA_DA_IDENTIDADE[linha % 4] ?? 'w-32') : (LARGURA_DE_TEXTO[(linha + coluna) % 4] ?? 'w-24');
  if (!alturaDupla) return <span className={`esqueleto block h-3 ${largura}`} />;
  /* Linha de duas informacoes: a segunda, de 12 px na celula de verdade, e a
     barra de 8. */
  return (
    <span className="flex flex-col gap-2">
      <span className={`esqueleto block h-3 ${largura}`} />
      <span className="esqueleto block h-2 w-16" />
    </span>
  );
}

/**
 * Esqueleto de carregamento (p.32): a FORMA da tabela, pulsando devagar, no
 * lugar exato onde ela vai chegar.
 *
 *  - O CABECALHO E O DE VERDADE. O nome das colunas nao depende da resposta,
 *    entao ele aparece desde o primeiro quadro, e quem abre a tela ja sabe o
 *    que vai ler. So as celulas sao esqueleto — e cada uma na forma do seu
 *    tipo: texto a esquerda, numero curto a direita, status como selo;
 *  - A MESMA ALTURA DA LINHA REAL (48 px) e o mesmo rodape, quando a tabela
 *    tem rodape: o dado chega e nada pula. A rodinha no meio da tela esta
 *    proibida justamente por nao reservar espaco;
 *  - ABAIXO DE 768 PX, CARTOES, como a lista de verdade fica nessa largura;
 *  - O CINZA PRECISA SER VISTO. Barra na cor da superficie rebaixada sobre o
 *    card branco media 1,06:1 e a tabela lia como caixa vazia — a "tabela em
 *    branco" que a regra 9 proibe. O utilitario `esqueleto` usa a `--borda`.
 *
 * Sem as colunas (so a quantidade), o cabecalho tambem vira barra.
 */
export function EsqueletoTabela({
  rotulo,
  colunas,
  linhas = 8,
  alturaDupla = false,
  comRodape = false,
}: {
  /** Do que e a lista; vira "Carregando ...", dito ao leitor de tela. */
  readonly rotulo: string;
  readonly colunas: number | readonly ColunaDoEsqueleto[];
  readonly linhas?: number;
  readonly alturaDupla?: boolean;
  readonly comRodape?: boolean;
}) {
  const cartoes = useAbaixoDe(768);
  const definidas: readonly ColunaDoEsqueleto[] =
    typeof colunas === 'number'
      ? Array.from({ length: colunas }, (_, i) => ({ chave: String(i), rotulo: '', alinhamento: 'texto' }))
      : colunas;
  const comNome = typeof colunas !== 'number';

  if (cartoes) {
    const [, ...resto] = definidas;
    const chaves = resto.filter((coluna) => coluna.prioridade !== 'secundaria');
    const temStatus = chaves.some((coluna) => coluna.alinhamento === 'status');
    const dados = chaves.filter((coluna) => coluna.alinhamento !== 'status').slice(0, 4);
    return (
      <div role="status" aria-busy="true">
        <span className="sr-only">{carregandoA(rotulo)}</span>
        <ul aria-hidden="true" className="flex flex-col">
          {Array.from({ length: Math.min(linhas, 4) }, (_, linha) => (
            <li key={linha} className="flex flex-col gap-3 border-t border-borda-sutil p-4 first:border-t-0">
              <span className={`esqueleto block h-4 ${LARGURA_DA_IDENTIDADE[linha % 4] ?? 'w-32'}`} />
              {temStatus ? <span className="esqueleto block h-6 w-24 rounded-pill" /> : null}
              {dados.length > 0 ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {dados.map((coluna, indice) => (
                    <div key={coluna.chave} className="flex min-w-0 flex-col gap-2">
                      {comNome ? (
                        <dt className="txt-legenda text-tinta-secundaria">{coluna.rotulo}</dt>
                      ) : (
                        <dt className="esqueleto block h-2 w-16" />
                      )}
                      <dd className={`esqueleto block h-3 ${LARGURA_DE_TEXTO[(linha + indice) % 4] ?? 'w-20'}`} />
                    </div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </ul>
        {comRodape ? <RodapeDoEsqueleto /> : null}
      </div>
    );
  }

  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">{carregandoA(rotulo)}</span>
      <div aria-hidden="true" className="overflow-hidden">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="h-[var(--tabela-cabecalho)] bg-rebaixada">
              {definidas.map((coluna, indice) => (
                <th
                  key={coluna.chave}
                  className={`txt-legenda whitespace-nowrap px-3 text-tinta-secundaria ${ALINHAMENTO_CABECALHO[coluna.alinhamento ?? 'texto']} ${indice > 0 && coluna.prioridade === 'secundaria' ? SECUNDARIA : ''}`}
                >
                  {comNome ? (
                    coluna.rotulo
                  ) : (
                    <span className={`esqueleto block h-2 w-16 ${coluna.alinhamento === 'numero' ? 'ml-auto' : ''}`} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: linhas }, (_, linha) => (
              <tr key={linha} className="h-[var(--linha-tabela)]">
                {definidas.map((coluna, indice) => (
                  <td
                    key={coluna.chave}
                    className={`border-t border-borda-sutil px-3 py-1 ${indice > 0 && coluna.prioridade === 'secundaria' ? SECUNDARIA : ''}`}
                  >
                    <BarraDaCelula
                      alinhamento={coluna.alinhamento ?? 'texto'}
                      coluna={indice}
                      linha={linha}
                      alturaDupla={alturaDupla}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {comRodape ? <RodapeDoEsqueleto /> : null}
    </div>
  );
}

/**
 * O rodape de contagem e paginacao (p.26), nas mesmas tres posicoes e com a
 * MESMA quebra do `RodapeTabela`: abaixo de 832 px do quadro, empilhado e
 * centralizado. Na grade de tres colunas a 390 px, a barra da contagem passava
 * da coluna dela e cobria a da paginacao.
 */
function RodapeDoEsqueleto() {
  return (
    <div aria-hidden="true" className="@container border-t border-borda-sutil">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 px-4 py-3 @max-[832px]:grid-cols-1 @max-[832px]:justify-items-center @max-[832px]:gap-2">
        <span className="esqueleto block h-3 w-40 max-w-full" />
        <span className="esqueleto block h-6 w-32 max-w-full" />
        <span className="@max-[832px]:hidden" />
      </div>
    </div>
  );
}

/**
 * Rodape (p.26): "Mostrando 1 a 10 de 42" a esquerda, a paginacao AO CENTRO e
 * os itens por pagina a direita. As tres posicoes sao fixas — uma grade
 * `1fr auto 1fr`, e nao um `space-between`, porque com `space-between` a
 * paginacao anda de lugar conforme o tamanho do texto da esquerda.
 *
 * A paginacao e a da p.26: setas nas pontas e o numero de cada pagina, com a
 * atual marcada; com muitas, as do meio viram reticencias. As setas sao so
 * icone, e por isso levam nome e dica: "Ver página anterior", "Ver próxima
 * página" — verbo e objeto, como todo rotulo de botao (p.21).
 *
 * Quando o quadro nao comporta as tres na mesma linha (conta pelo QUADRO, e
 * nao pela janela: com o painel aberto a tabela estreita), elas empilham.
 */
const BOTAO_DE_PAGINA =
  'txt-botao inline-flex h-[var(--controle-p)] min-w-[var(--controle-p)] cursor-pointer items-center justify-center rounded-etiqueta px-2 tabular-nums text-tinta-lista hover:bg-rebaixada disabled:cursor-not-allowed disabled:text-tinta-desabilitada disabled:hover:bg-transparent';

/** As paginas a mostrar: todas ate sete; depois, as pontas, a atual e as vizinhas. */
function paginasVisiveis(atual: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (atual <= 4) return [1, 2, 3, 4, 5, null, total];
  if (atual >= total - 3) return [1, null, total - 4, total - 3, total - 2, total - 1, total];
  return [1, null, atual - 1, atual, atual + 1, null, total];
}

/**
 * O rodapé SÓ DE CONTAGEM (p.26), para a lista curta por natureza — uma linha
 * por instrumento, por caso fixo, por pergunta do teste: ela nunca passa de
 * uma página, e setas de paginação desligadas seriam controle que não faz
 * nada. A contagem fica, na mesma posição e na mesma frase do `RodapeTabela`.
 */
export function RodapeDeContagem({ total }: { readonly total: number }) {
  return (
    <div className="flex h-[var(--linha-tabela)] items-center border-t border-borda-sutil px-4">
      <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
        {total === 0 ? 'Mostrando 0 de 0' : `Mostrando 1 a ${total} de ${total}`}
      </span>
    </div>
  );
}

export function RodapeTabela({
  primeiro,
  ultimo,
  total,
  pagina,
  paginas,
  porPagina,
  opcoesPorPagina,
  aoMudarPagina,
  aoMudarPorPagina,
}: {
  readonly primeiro: number;
  readonly ultimo: number;
  readonly total: number;
  readonly pagina: number;
  readonly paginas: number;
  readonly porPagina: number;
  readonly opcoesPorPagina: readonly number[];
  readonly aoMudarPagina: (pagina: number) => void;
  readonly aoMudarPorPagina: (porPagina: number) => void;
}) {
  const ultima = Math.max(paginas, 1);
  return (
    <div className="@container border-t border-borda-sutil">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 px-4 py-2 @max-[832px]:grid-cols-1 @max-[832px]:justify-items-center @max-[832px]:gap-2">
        <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
          {total === 0 ? 'Mostrando 0 de 0' : `Mostrando ${primeiro} a ${ultimo} de ${total}`}
        </span>

        <nav aria-label="Páginas da lista" className="flex flex-wrap items-center justify-center gap-1">
          <button
            type="button"
            className={BOTAO_DE_PAGINA}
            aria-label="Ver página anterior"
            title="Ver página anterior"
            disabled={pagina <= 1}
            onClick={() => {
              aoMudarPagina(pagina - 1);
            }}
          >
            <ChevronLeft aria-hidden="true" className="size-[var(--icone-controle)]" />
          </button>
          {paginasVisiveis(pagina, ultima).map((numero, indice) =>
            numero === null ? (
              <span key={`reticencias-${String(indice)}`} aria-hidden="true" className="txt-botao px-1 text-tinta-apagada">
                …
              </span>
            ) : (
              <button
                key={numero}
                type="button"
                aria-label={`Ver página ${String(numero)} de ${String(ultima)}`}
                {...(numero === pagina ? { 'aria-current': 'page' as const } : {})}
                className={`${BOTAO_DE_PAGINA} ${numero === pagina ? 'bg-acao text-acao-rotulo hover:bg-acao' : ''}`}
                onClick={() => {
                  aoMudarPagina(numero);
                }}
              >
                {numero}
              </button>
            ),
          )}
          <button
            type="button"
            className={BOTAO_DE_PAGINA}
            aria-label="Ver próxima página"
            title="Ver próxima página"
            disabled={pagina >= ultima}
            onClick={() => {
              aoMudarPagina(pagina + 1);
            }}
          >
            <ChevronRight aria-hidden="true" className="size-[var(--icone-controle)]" />
          </button>
        </nav>

        <label className="txt-apoio flex items-center justify-end gap-2 whitespace-nowrap text-tinta-secundaria">
          Itens por página
          <select
            className="controle h-[var(--controle-p)]"
            value={porPagina}
            onChange={(evento) => {
              aoMudarPorPagina(Number(evento.target.value));
            }}
          >
            {opcoesPorPagina.map((quantidade) => (
              <option key={quantidade} value={quantidade}>
                {quantidade}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
